import { useState } from 'react';
import { useStore } from '@nanostores/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  $editHistory,
  clearEditHistory,
  formatTimestamp,
  logRestoreEdit,
  type EditHistoryEntry,
} from '~/lib/stores/edit-history';
import { applyTextEditDirect } from '~/lib/element-selector/direct-edit';
import { trackVisualEdit } from '~/lib/element-selector/analytics';
import { apiReadSandboxFile, apiWriteSandboxFile } from '~/lib/api/sandbox-files';
import { classNames } from '~/utils/classNames';

interface EditHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onReloadPreview: () => void;
}

function EditTypeIcon({ type }: { type: EditHistoryEntry['type'] }) {
  const iconClass = {
    style: 'i-ph:palette',
    text: 'i-ph:text-t',
    image: 'i-ph:image',
  }[type];

  return <div className={classNames(iconClass, 'text-sm')} />;
}

function VersionEntry({
  entry,
  versionNumber,
  isCurrentVersion,
  isExpanded,
  onToggle,
  onRestore,
  isRestoring,
}: {
  entry: EditHistoryEntry;
  versionNumber: number;
  isCurrentVersion: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onRestore: (entry: EditHistoryEntry, versionNumber: number) => void;
  isRestoring: boolean;
}) {
  const fileName = entry.file.split('/').pop() || entry.file;
  const isFailed = entry.status === 'failed';

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={classNames(
        'rounded-xl border transition-all duration-200',
        isFailed
          ? 'bg-danger/5 border-danger/20'
          : isCurrentVersion
            ? 'bg-accent/5 border-accent/20'
            : 'bg-raised/50 border-hairline',
      )}
    >
      {/* Clickable Header - Always visible, same size for all */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-3 flex items-center gap-3 text-left hover:bg-white/[0.02] transition-colors rounded-xl"
      >
        {/* Type icon */}
        <div
          className={classNames(
            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
            isFailed
              ? 'bg-danger/10 text-danger'
              : isCurrentVersion
                ? 'bg-accent/10 text-accent'
                : 'bg-raised text-secondary',
          )}
        >
          <EditTypeIcon type={entry.type} />
        </div>

        {/* Version info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={classNames('text-xs font-medium', isCurrentVersion ? 'text-accent' : 'text-primary')}>
              Dunea v{versionNumber}
            </span>
            {isCurrentVersion && (
              <span className="px-1.5 py-0.5 rounded bg-accent/10 text-[8px] text-accent font-medium uppercase tracking-wide">
                Current
              </span>
            )}
            {isFailed && (
              <span className="px-1.5 py-0.5 rounded bg-danger/10 text-[8px] text-danger font-medium">Failed</span>
            )}
          </div>
          <span className="text-[10px] text-muted">{formatTimestamp(entry.timestamp)}</span>
        </div>

        {/* Expand/collapse chevron */}
        <div
          className={classNames(
            'i-ph:caret-down text-sm text-muted transition-transform duration-200',
            isExpanded && 'rotate-180',
          )}
        />
      </button>

      {/* Expandable content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-0">
              {/* Description */}
              <div className="flex items-center gap-2 mb-2 pl-11">
                <span className="text-[11px] text-secondary">{entry.description}</span>
              </div>

              {/* File info */}
              <div className="flex items-center gap-2 text-[10px] text-muted mb-3 pl-11">
                <div className="i-ph:file-code text-xs" />
                <code className="font-mono bg-raised/50 px-1.5 py-0.5 rounded">
                  {fileName}:{entry.line}
                </code>
              </div>

              {/* Changes */}
              {entry.changes.length > 0 && (
                <div className="bg-surface/50 rounded-lg p-2.5 mb-3 ml-11">
                  <div className="text-[9px] text-muted uppercase tracking-wide mb-1.5 font-medium">Changes</div>
                  <div className="flex flex-col gap-1.5">
                    {entry.changes.map((change, i) => (
                      <div key={i} className="flex items-center gap-2 text-[10px]">
                        <span className="text-muted w-14 truncate font-medium uppercase tracking-wide text-[9px]">
                          {change.property}
                        </span>
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <code className="px-1 py-0.5 rounded bg-danger/10 text-danger/80 font-mono truncate max-w-[60px] text-[9px]">
                            {change.oldValue || '—'}
                          </code>
                          <div className="i-ph:arrow-right text-faint text-[8px] flex-shrink-0" />
                          <code className="px-1 py-0.5 rounded bg-success/10 text-success font-mono truncate max-w-[60px] text-[9px]">
                            {change.newValue || '—'}
                          </code>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error message */}
              {entry.error && (
                <div className="px-2.5 py-2 rounded-lg bg-danger/10 text-[10px] text-danger border border-danger/20 mb-3 ml-11">
                  {entry.error}
                </div>
              )}

              {/* Restore button - show for all versions except current */}
              {!isCurrentVersion && entry.status !== 'failed' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore(entry, versionNumber);
                  }}
                  disabled={isRestoring}
                  className={classNames(
                    'w-full flex items-center justify-center gap-2 h-8 rounded-lg text-xs font-medium transition-all duration-200 ml-11',
                    'max-w-[calc(100%-44px)]',
                    isRestoring
                      ? 'bg-raised/50 text-muted cursor-not-allowed'
                      : 'bg-accent/10 hover:bg-accent/20 border border-accent/20 text-accent',
                  )}
                >
                  {isRestoring ? (
                    <>
                      <div className="i-svg-spinners:90-ring-with-bg text-xs" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <div className="i-ph:clock-counter-clockwise text-sm" />
                      Restore to v{versionNumber}
                    </>
                  )}
                </button>
              )}

              {/* Current version indicator */}
              {isCurrentVersion && (
                <div className="flex items-center justify-center gap-2 h-8 rounded-lg bg-accent/5 border border-accent/10 text-[10px] text-accent/70 ml-11 max-w-[calc(100%-44px)]">
                  <div className="i-ph:check-circle text-sm" />
                  Current version
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function EditHistoryPanel({ isOpen, onClose, onReloadPreview }: EditHistoryPanelProps) {
  const history = useStore($editHistory);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filter out failed entries for version calculation
  const successfulHistory = history.filter((e) => e.status !== 'failed');
  const totalVersions = history.length;

  // Find the current state - the most recent successful entry that hasn't been superseded
  // The "current" version is always the latest successful one
  const currentEntry = successfulHistory[0];

  /**
   * Restore to a specific version
   *
   * Strategy:
   * - For style edits: We need to find the CURRENT className in the file and replace it
   *   with the target version's classesBefore (what the element looked like BEFORE that edit)
   * - For text edits: Similar - find current text and replace with target's fullOldValue
   *
   * Example: v1 → v2 → v3 (current)
   * - v1's classesBefore = "original-classes"
   * - v1's classesAfter = "after-v1-edit"
   * - v2's classesBefore = "after-v1-edit"
   * - v2's classesAfter = "after-v2-edit"
   * - v3's classesBefore = "after-v2-edit"
   * - v3's classesAfter = "current-in-file"
   *
   * To restore to v1: Find "current-in-file", replace with "original-classes"
   * To restore to v2: Find "current-in-file", replace with "after-v1-edit"
   */
  const handleRestore = async (targetEntry: EditHistoryEntry, _targetVersion: number) => {
    if (!currentEntry?.chatId) {
      return;
    }

    setRestoringId(targetEntry.id);
    setRestoreError(null);

    try {
      // The target state is the classesAfter of the target entry
      // (what the element looked like AFTER that version's edit was applied)
      // This means "Restore to v1" gives you the result of v1, not what was before v1
      const targetClasses = targetEntry.classesAfter;

      if (targetEntry.type === 'style') {
        if (!targetClasses) {
          console.error('[EditHistoryPanel] Missing target classes for restore');
          setRestoreError('Missing class data for restore');

          return;
        }

        // Read the file
        const content = await apiReadSandboxFile(currentEntry.chatId, targetEntry.file);

        // Find the CURRENT className in the file by checking all entries' classesAfter
        // The most recent entry with classesAfter for this file should have the current value
        let currentClasses: string | null = null;

        for (const entry of history) {
          if (entry.file === targetEntry.file && entry.classesAfter && entry.status !== 'failed') {
            currentClasses = entry.classesAfter;
            break;
          }
        }

        if (!currentClasses) {
          console.error('[EditHistoryPanel] Could not determine current classes from history');
          setRestoreError('Cannot restore: unable to find current element state');

          return;
        }

        // Find the current className in various quote styles
        const candidates = [
          { needle: `className="${currentClasses}"`, replacement: `className="${targetClasses}"` },
          { needle: `className='${currentClasses}'`, replacement: `className='${targetClasses}'` },
          { needle: `className={\`${currentClasses}\`}`, replacement: `className={\`${targetClasses}\`}` },
        ];

        let newContent: string | null = null;

        for (const { needle, replacement } of candidates) {
          const idx = content.indexOf(needle);

          if (idx !== -1) {
            if (content.indexOf(needle, idx + 1) !== -1) {
              console.error('[EditHistoryPanel] className not unique, cannot restore safely');
              trackVisualEdit('canvas', 'restore', { ok: false, reason: 'className is not unique in the file' });
              setRestoreError('Cannot restore: className appears multiple times in file');

              return;
            }

            newContent = content.slice(0, idx) + replacement + content.slice(idx + needle.length);
            break;
          }
        }

        if (!newContent) {
          console.error('[EditHistoryPanel] Could not find current className in file:', currentClasses);
          trackVisualEdit('canvas', 'restore', { ok: false, reason: 'className literal not found in source' });
          setRestoreError('Cannot restore: element has been modified outside Canvas');

          return;
        }

        // Write the restored content
        await apiWriteSandboxFile(currentEntry.chatId, targetEntry.file, newContent);
        trackVisualEdit('canvas', 'restore', { ok: true });

        // Log the restore as a new version (preserves history)
        logRestoreEdit(currentEntry.chatId, targetEntry, targetClasses);
        onReloadPreview();
        onClose();
      } else if (targetEntry.type === 'text') {
        // For text restore, we need to:
        // 1. Find what text is currently in the file
        // 2. Replace it with the target version's fullNewValue (the RESULT of that edit)
        // This means "Restore to v1" gives you v1's text result

        // The target's fullNewValue is what we want to restore TO (the result of that version)
        const targetTextChange = targetEntry.changes[0];

        if (!targetTextChange?.fullNewValue) {
          setRestoreError('Missing target text data for restore');
          return;
        }

        // Read the file to find what to replace
        const content = await apiReadSandboxFile(currentEntry.chatId, targetEntry.file);

        // Find the most recent text edit for the same file to get the current text
        // History is ordered newest-first, so we search from the start
        const mostRecentTextEdit = history.find(
          (e) => e.type === 'text' && e.file === targetEntry.file && e.status !== 'failed',
        );

        let textToFind: string | null = null;

        // Try the most recent text edit's fullNewValue first (most likely to be current)
        if (mostRecentTextEdit?.changes[0]?.fullNewValue) {
          const trimmed = mostRecentTextEdit.changes[0].fullNewValue.trim();
          const occurrences = content.split(trimmed).length - 1;

          if (occurrences === 1) {
            textToFind = trimmed;
          }
        }

        // If not found, try any other text entries for the same file
        if (!textToFind) {
          for (const entry of history) {
            if (entry.type === 'text' && entry.file === targetEntry.file && entry.status !== 'failed') {
              const val = entry.changes[0]?.fullNewValue?.trim();

              if (val) {
                const occurrences = content.split(val).length - 1;

                if (occurrences === 1) {
                  textToFind = val;
                  break;
                }
              }
            }
          }
        }

        if (!textToFind) {
          setRestoreError('Cannot find current text in file - it may have been modified outside Canvas');
          return;
        }

        const selection = {
          file: targetEntry.file,
          line: targetEntry.line,
          tag: targetEntry.tag,
          classes: '',
          textSnippet: '',
          computedStyles: {},
        };

        // Replace found text with target's result text (what v1 changed it TO)
        const targetText = targetTextChange.fullNewValue.trim();
        const result = await applyTextEditDirect(currentEntry.chatId, selection, textToFind, targetText);
        trackVisualEdit('canvas', 'restore', result);

        if (result.ok) {
          // Log the restore as a new version (preserves history)
          logRestoreEdit(currentEntry.chatId, targetEntry, targetEntry.classesAfter || '', targetText);
          onReloadPreview();
          onClose();
        } else {
          console.error('[EditHistoryPanel] Text restore failed:', result.reason);
          setRestoreError(`Text restore failed: ${result.reason}`);
        }
      }
    } catch (err) {
      console.error('[EditHistoryPanel] Restore error:', err);
      setRestoreError(`Restore failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200]"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[380px] bg-surface border-l border-hairline z-[201] flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
          >
            {/* Header */}
            <div className="flex-shrink-0 px-5 py-5 border-b border-hairline">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <div className="i-ph:git-branch text-accent text-xl" />
                  </div>
                  <div>
                    <h2 className="text-sm font-medium text-primary">Version History</h2>
                    <p className="text-[10px] text-muted">
                      {totalVersions} version{totalVersions !== 1 ? 's' : ''} · Restore any version
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close version history"
                  className="w-9 h-9 touch:w-10 touch:h-10 rounded-lg flex items-center justify-center hover:bg-raised transition-colors"
                >
                  <div className="i-ph:x text-lg text-secondary" />
                </button>
              </div>

              {/* Info banner */}
              {history.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-raised/50 border border-hairline">
                  <div className="i-ph:info text-muted text-sm" />
                  <span className="text-[10px] text-muted">Click to expand details and restore</span>
                </div>
              )}
            </div>

            {/* Restore error banner */}
            {restoreError && (
              <div className="mx-4 mt-4 px-3 py-2 rounded-lg bg-danger/10 border border-danger/20 flex items-center gap-2">
                <div className="i-ph:warning text-danger text-sm flex-shrink-0" />
                <span className="text-[11px] text-danger flex-1">{restoreError}</span>
                <button
                  type="button"
                  onClick={() => setRestoreError(null)}
                  className="w-5 h-5 rounded flex items-center justify-center hover:bg-danger/20 transition-colors"
                >
                  <div className="i-ph:x text-danger text-xs" />
                </button>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {history.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <div className="w-20 h-20 rounded-2xl bg-raised/50 flex items-center justify-center mb-6">
                    <div className="i-ph:git-branch text-4xl text-faint" />
                  </div>
                  <h3 className="text-sm font-medium text-secondary mb-2">No versions yet</h3>
                  <p className="text-xs text-muted leading-relaxed max-w-[240px]">
                    Each edit creates a new version. You can restore any previous version anytime.
                  </p>
                </div>
              ) : (
                <div className="p-4 flex flex-col gap-2">
                  {history.map((entry, index) => {
                    // Version numbers: newest entry = highest version
                    const versionNumber = totalVersions - index;
                    const isCurrentVersion = entry.id === currentEntry?.id;

                    return (
                      <VersionEntry
                        key={entry.id}
                        entry={entry}
                        versionNumber={versionNumber}
                        isCurrentVersion={isCurrentVersion}
                        isExpanded={expandedId === entry.id}
                        onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                        onRestore={handleRestore}
                        isRestoring={restoringId === entry.id}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {history.length > 0 && (
              <div className="flex-shrink-0 p-4 border-t border-hairline">
                <button
                  type="button"
                  onClick={() => clearEditHistory()}
                  className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-raised hover:bg-elevated border border-hairline text-sm text-secondary hover:text-primary transition-colors"
                >
                  <div className="i-ph:trash text-sm" />
                  Clear History
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
